import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Upload, 
  Users, 
  HardDrive, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Activity
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { SystemMetrics } from '../types';

const Dashboard: React.FC = () => {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: adminApi.getDashboardStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['system-metrics'],
    queryFn: adminApi.getSystemMetrics,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  if (statsLoading || metricsLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back! Here's what's happening with your VOD system.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Activity className="h-4 w-4 text-green-500" />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Uploads"
          value={stats?.uploads.total || 0}
          change={`+${stats?.uploads.today || 0} today`}
          icon={Upload}
          color="blue"
        />
        <MetricCard
          title="Active Users"
          value={stats?.users.active || 0}
          change={`${stats?.users.total || 0} total`}
          icon={Users}
          color="green"
        />
        <MetricCard
          title="Storage Used"
          value={formatBytes(stats?.storage.used || 0)}
          change={`${stats?.storage.percentage || 0}% of total`}
          icon={HardDrive}
          color="purple"
        />
        <MetricCard
          title="Failed Uploads"
          value={stats?.uploads.failed || 0}
          change={`${stats?.uploads.pending || 0} pending`}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Upload Status Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Status</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Completed', value: stats?.uploads.completed || 0, color: '#10b981' },
                    { name: 'Processing', value: stats?.uploads.processing || 0, color: '#f59e0b' },
                    { name: 'Failed', value: stats?.uploads.failed || 0, color: '#ef4444' },
                    { name: 'Pending', value: stats?.uploads.pending || 0, color: '#6b7280' },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { name: 'Completed', value: stats?.uploads.completed || 0, color: '#10b981' },
                    { name: 'Processing', value: stats?.uploads.processing || 0, color: '#f59e0b' },
                    { name: 'Failed', value: stats?.uploads.failed || 0, color: '#ef4444' },
                    { name: 'Pending', value: stats?.uploads.pending || 0, color: '#6b7280' },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* System Resources */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Resources</h3>
          <div className="space-y-4">
            <ResourceBar
              label="Memory Usage"
              value={metrics?.memoryUsage.used || 0}
              max={metrics?.memoryUsage.total || 100}
              color="blue"
            />
            <ResourceBar
              label="CPU Usage"
              value={metrics?.cpuUsage || 0}
              max={100}
              color="green"
            />
            <ResourceBar
              label="Storage Usage"
              value={stats?.storage.used || 0}
              max={stats?.storage.total || 100}
              color="purple"
            />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RecentUploads />
        </div>
        <div>
          <SystemHealth metrics={metrics} />
        </div>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: number | string;
  change: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'red';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
  };

  return (
    <div className="card p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`${colorClasses[color]} rounded-md p-3`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-lg font-semibold text-gray-900">{value}</dd>
          </dl>
          <div className="flex items-center mt-1">
            <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            <p className="text-sm text-gray-500">{change}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ResourceBarProps {
  label: string;
  value: number;
  max: number;
  color: 'blue' | 'green' | 'purple';
}

const ResourceBar: React.FC<ResourceBarProps> = ({ label, value, max, color }) => {
  const percentage = (value / max) * 100;
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
  };

  return (
    <div>
      <div className="flex justify-between text-sm font-medium text-gray-900 mb-1">
        <span>{label}</span>
        <span>{percentage.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${colorClasses[color]} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{typeof value === 'number' ? formatBytes(value) : value}</span>
        <span>{typeof max === 'number' ? formatBytes(max) : max}</span>
      </div>
    </div>
  );
};

const RecentUploads: React.FC = () => {
  const { data: uploads } = useQuery({
    queryKey: ['recent-uploads'],
    queryFn: () => adminApi.getUploads({ limit: 5 }),
    refetchInterval: 15000,
  });

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Uploads</h3>
      <div className="space-y-3">
        {uploads?.items.map((upload) => (
          <div key={upload.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <StatusIcon status={upload.status} />
              <div>
                <p className="font-medium text-gray-900 truncate max-w-xs">{upload.filename}</p>
                <p className="text-sm text-gray-500">
                  {upload.metadata.streamTitle || 'No title'} • {formatBytes(upload.originalSize)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{upload.progress}%</p>
              <p className="text-xs text-gray-500">{new Date(upload.startedAt).toLocaleTimeString()}</p>
            </div>
          </div>
        ))}
        
        {(!uploads?.items || uploads.items.length === 0) && (
          <div className="text-center py-6 text-gray-500">
            <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No recent uploads</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SystemHealth: React.FC<{ metrics?: SystemMetrics }> = ({ metrics }) => {
  const uptime = metrics?.uptime || 0;
  const uptimeFormatted = formatUptime(uptime);

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Uptime</span>
          <span className="text-sm font-medium text-gray-900">{uptimeFormatted}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Total Uploads</span>
          <span className="text-sm font-medium text-gray-900">{metrics?.totalUploads || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Active Jobs</span>
          <span className="text-sm font-medium text-gray-900">{metrics?.activeUploads || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Avg Processing Time</span>
          <span className="text-sm font-medium text-gray-900">
            {formatDuration(metrics?.averageProcessingTime || 0)}
          </span>
        </div>
        
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="h-2 w-2 bg-green-500 rounded-full mr-2" />
            <span className="text-sm text-green-600 font-medium">All systems operational</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'processing':
      return <Clock className="h-5 w-5 text-yellow-500" />;
    case 'failed':
      return <AlertTriangle className="h-5 w-5 text-red-500" />;
    default:
      return <Clock className="h-5 w-5 text-gray-500" />;
  }
};

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-1/4" />
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="card p-5">
          <div className="h-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="card p-6">
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  </div>
);

// Utility functions
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

export default Dashboard;